import React from 'react';
import { Input } from '../../components/ui/input';
import { Sparkles, Loader2 } from 'lucide-react';
import { MultiSelect } from './MultiSelect';

export const CAMPAIGN_TYPES = ['Non-collab Reel', 'Collab Reel', 'Story', 'Reel + Story', 'UGC', 'YouTube Integration', 'Multi-platform'];
export const CATEGORIES = ['Food', 'Comedy', 'Entertainment', 'Lifestyle', 'Fashion', 'Beauty', 'Travel', 'Parenting', 'Fitness', 'Tech', 'Gaming', 'Finance'];
const QUALITY = [['LUXE', 'LUXE (A+)'], ['PREMIUM', 'PREMIUM (A)'], ['GOOD', 'GOOD (B)'], ['MASSY', 'MASSY (C)']];
const EXCLUSIVITY = ['Exclusive', 'Exclusive Trial', 'Non Exclusive', 'HUL Loreal', 'Remaining'];

export const EMPTY_BRIEF = {
  brand_name: '', campaign_type: CAMPAIGN_TYPES[0], categories: [], min_followers: '', max_followers: '',
  quality_tiers: [], exclusivity: [], budget_per_creator: '', city: [], state: [], gender: 'any', max_creators: 30,
};

const LABEL = 'text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white';
const SELECT = 'w-full h-9 rounded-md px-3 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white';

const Chips = ({ options, value, onToggle, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(([val, label]) => {
      const on = value.includes(val);
      return (
        <button
          key={val}
          type="button"
          onClick={() => onToggle(val)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-[#FF451A] border-[#FF451A] text-white' : 'bg-white dark:bg-[#1A1D24] border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white hover:border-[#FF451A]/50'}`}
          data-testid={`${testid}-${val.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export const BriefForm = ({ brief, setBrief, onSubmit, loading, filters }) => {
  const set = (k) => (e) => setBrief((b) => ({ ...b, [k]: e.target.value }));
  const toggle = (k) => (v) => setBrief((b) => ({ ...b, [k]: b[k].includes(v) ? b[k].filter((x) => x !== v) : [...b[k], v] }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="bg-white dark:bg-[#0F1115] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 space-y-5"
      data-testid="ideation-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={LABEL}>Brand Name</label>
          <Input value={brief.brand_name} onChange={set('brand_name')} placeholder="e.g. Zomato" data-testid="ideation-brand-input" />
        </div>
        <div>
          <label className={LABEL}>Campaign Type</label>
          <select value={brief.campaign_type} onChange={set('campaign_type')} className={SELECT} data-testid="ideation-type-select">
            {CAMPAIGN_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL}>Max Creators</label>
          <Input type="number" min={1} max={500} value={brief.max_creators} onChange={set('max_creators')} data-testid="ideation-max-input" />
        </div>
      </div>

      <div>
        <label className={LABEL}>Content Category Fit</label>
        <Chips options={CATEGORIES.map((c) => [c, c])} value={brief.categories} onToggle={toggle('categories')} testid="ideation-cat" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={LABEL}>Quality Grade</label>
          <Chips options={QUALITY} value={brief.quality_tiers} onToggle={toggle('quality_tiers')} testid="ideation-quality" />
        </div>
        <div>
          <label className={LABEL}>Exclusivity</label>
          <Chips options={EXCLUSIVITY.map((e) => [e, e])} value={brief.exclusivity} onToggle={toggle('exclusivity')} testid="ideation-excl" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className={LABEL}>Min Followers</label>
          <Input type="number" value={brief.min_followers} onChange={set('min_followers')} placeholder="10000" data-testid="ideation-min-followers" />
        </div>
        <div>
          <label className={LABEL}>Max Followers</label>
          <Input type="number" value={brief.max_followers} onChange={set('max_followers')} placeholder="500000" data-testid="ideation-max-followers" />
        </div>
        <div>
          <label className={LABEL}>Budget / Creator (₹)</label>
          <Input type="number" value={brief.budget_per_creator} onChange={set('budget_per_creator')} placeholder="Optional" data-testid="ideation-budget-input" />
          <p className="text-[10px] text-[#6B6B6B] dark:text-zinc-400 mt-1">Max cost for the selected deliverable. Creators without pricing are included.</p>
        </div>
        <div className="col-span-2 md:col-span-2">
          <label className={LABEL}>City</label>
          <MultiSelect value={brief.city} onChange={(v) => setBrief((b) => ({ ...b, city: v }))} options={filters.cities} testid="ideation-city-select" />
        </div>
        <div className="col-span-2 md:col-span-1">
          <label className={LABEL}>State</label>
          <MultiSelect value={brief.state} onChange={(v) => setBrief((b) => ({ ...b, state: v }))} options={filters.states} testid="ideation-state-select" />
        </div>
        <div>
          <label className={LABEL}>Gender</label>
          <select value={brief.gender} onChange={set('gender')} className={SELECT} data-testid="ideation-gender-select">
            <option value="any">Any</option><option value="male">Male</option><option value="female">Female</option>
          </select>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="rounded-full px-6 py-2.5 bg-[#FF451A] hover:bg-[#E63E17] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
        data-testid="ideation-generate-btn"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate Shortlist
      </button>
    </form>
  );
};
